import API_BASE from '../config';
import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const URGENCIES = ["All Urgency", "CRITICAL", "HIGH", "MEDIUM"];

const urgencyColor: Record<string, string> = {
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
  HIGH:     "bg-amber-50 text-amber-700 border-amber-200",
  MEDIUM:   "bg-blue-50 text-blue-700 border-blue-200",
};

// ─── Framework Card ────────────────────────────────────────────────────────────
function FrameworkCard({ f, onSignUp }: { f: any; onSignUp: () => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 hover:border-red-100
        hover:shadow-lg hover:shadow-red-50 transition-all duration-200 overflow-hidden"
    >
      <div className="p-5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-black text-slate-900 uppercase tracking-tight">
                  {f.name}
                </p>
                <span className={`text-[9px] font-black uppercase tracking-widest
                  px-2 py-0.5 rounded-full border ${urgencyColor[f.urgency] || urgencyColor["MEDIUM"]}`}>
                  {f.urgency}
                </span>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                {f.region}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest
              bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-lg hidden sm:block">
              ✓ Covered
            </span>
            <ChevronDown
              size={16}
              className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 font-medium mt-3 leading-relaxed">
          {f.description}
        </p>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-slate-50 pt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                    Region
                  </p>
                  <p className="text-xs font-bold text-slate-700">{f.region}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mb-1">
                    Fine Risk
                  </p>
                  <p className="text-xs font-bold text-red-700">{f.fine_risk}</p>
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1">
                  Action Required Within
                </p>
                <p className="text-xs font-bold text-amber-700">{f.deadline_days} days</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onSignUp(); }}
                className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-[10px]
                  font-black uppercase tracking-widest hover:bg-red-600 transition-all
                  flex items-center justify-center gap-2"
              >
                Check Your Compliance → Free Trial
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Frameworks() {
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [region,     setRegion]     = useState("All Regions");
  const [urgency,    setUrgency]    = useState("All Urgency");

  useEffect(() => {
    fetch(`${API_BASE}/api/frameworks/all`)
      .then(r => r.json())
      .then(data => {
        setFrameworks(data.frameworks || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const regions = useMemo(() => {
    const unique = Array.from(new Set(frameworks.map(f => f.region)));
    return ["All Regions", ...unique.sort()];
  }, [frameworks]);

  const filtered = useMemo(() => {
    return frameworks.filter(f => {
      const matchSearch  = !search  || f.name.toLowerCase().includes(search.toLowerCase())
                                    || f.description.toLowerCase().includes(search.toLowerCase());
      const matchRegion  = region  === "All Regions"  || f.region === region;
      const matchUrgency = urgency === "All Urgency"  || f.urgency === urgency;
      return matchSearch && matchRegion && matchUrgency;
    });
  }, [frameworks, search, region, urgency]);

  const handleSignUp = () => navigate("/login", { state: { initialMode: "signup" } });

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    frameworks.forEach(f => {
      counts[f.region] = (counts[f.region] || 0) + 1;
    });
    return counts;
  }, [frameworks]);

  if (loading) return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
      <div className="text-white text-xl font-black tracking-widest uppercase">Loading Frameworks...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] font-['Inter']">

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <div className="bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: "32px 32px" }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600
          opacity-[0.06] rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-24">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-400 hover:text-white
              transition-colors text-[10px] font-black uppercase tracking-widest mb-8"
          >
            ← Back to VicShield
          </button>

          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">
            Regulatory Intelligence
          </p>
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter
            leading-tight uppercase mb-6">
            {frameworks.length}+ Frameworks.<br />
            <span className="text-red-500">{regions.length - 1} Countries.</span><br />
            One Platform.
          </h1>
          <p className="text-slate-300 text-lg font-medium max-w-2xl leading-relaxed mb-8">
            VicShield automatically detects which regulations apply to your business
            based on your sector, geography and customer base — then checks your
            compliance across all of them simultaneously.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { value: `${frameworks.length}+`, label: "Frameworks Covered" },
              { value: `${regions.length - 1}`,  label: "Countries & Jurisdictions" },
              { value: "34",                      label: "Sectors Covered" },
              { value: "100%",                    label: "Automated Detection" },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <p className="text-2xl font-black text-white tracking-tighter">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleSignUp}
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600
              text-white rounded-2xl font-black text-[11px] uppercase tracking-widest
              hover:bg-white hover:text-slate-900 transition-all shadow-xl shadow-red-900/30"
          >
            Find Your Applicable Frameworks — Free
            <ArrowRight size={15} />
          </button>
        </div>
      </div>

      {/* ── REGION QUICK FILTER ───────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {regions.map(r => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase
                  tracking-widest transition-all whitespace-nowrap border
                  ${region === r
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-500 border-slate-100 hover:border-red-200 hover:text-red-600"
                  }`}
              >
                {r === "All Regions" ? "All" : r}
                {r !== "All Regions" && regionCounts[r] && (
                  <span className="ml-1 opacity-60">({regionCounts[r]})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── SEARCH + FILTER ───────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-3 flex-wrap md:flex-nowrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search regulation, region..."
              className="w-full pl-11 pr-4 py-3.5 bg-white border-2 border-slate-100
                rounded-2xl text-sm font-medium text-slate-900 placeholder:text-slate-300
                focus:outline-none focus:border-red-400 transition-all"
            />
            {search && (
              <button onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={14} />
              </button>
            )}
          </div>

          <select
            value={urgency}
            onChange={e => setUrgency(e.target.value)}
            className="px-4 py-3.5 bg-white border-2 border-slate-100 rounded-2xl
              text-sm font-bold text-slate-700 focus:outline-none focus:border-red-400
              transition-all cursor-pointer"
          >
            {URGENCIES.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between mt-4 mb-6">
          <p className="text-sm font-bold text-slate-500">
            Showing <span className="text-slate-900 font-black">{filtered.length}</span> of{" "}
            <span className="text-slate-900 font-black">{frameworks.length}</span> frameworks
            {search && <span className="text-red-600"> for "{search}"</span>}
          </p>
          {(search || region !== "All Regions" || urgency !== "All Urgency") && (
            <button
              onClick={() => { setSearch(""); setRegion("All Regions"); setUrgency("All Urgency"); }}
              className="text-[10px] font-black uppercase tracking-widest text-red-600
                hover:text-slate-900 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((f) => (
              <FrameworkCard key={f.id} f={f} onSignUp={handleSignUp} />
            ))}
          </AnimatePresence>
          {filtered.length === 0 && (
            <div className="col-span-3 text-center py-20">
              <p className="text-4xl mb-4">🔍</p>
              <p className="text-lg font-black text-slate-900 uppercase tracking-tight mb-2">
                No frameworks found
              </p>
              <p className="text-sm font-medium text-slate-400">
                Try a different search term or clear your filters
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM CTA ────────────────────────────────────────────────────── */}
      <div className="bg-[#0f172a] mt-16">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">
            Automatic Detection
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter
            uppercase mb-6">
            Not Sure Which Frameworks Apply To You?
          </h2>
          <p className="text-slate-300 font-medium leading-relaxed mb-8 max-w-2xl mx-auto">
            VicShield analyses your sector, geography, revenue size and customer base
            to automatically identify every applicable regulation — then checks your
            compliance against all of them in one 15-minute assessment.
          </p>
          <button
            onClick={handleSignUp}
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600
              text-white rounded-2xl font-black text-[11px] uppercase tracking-widest
              hover:bg-white hover:text-slate-900 transition-all shadow-xl shadow-red-900/30"
          >
            Start Free Assessment — Takes 15 Minutes
            <ArrowRight size={15} />
          </button>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-4">
            7-day free trial · Card not charged until day 8 · Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}